// Crystal Quiz Data - Practical and Psychological Questions
import { crystals } from './crystalData';

export interface QuizQuestion {
  id: string;
  type: 'name' | 'property' | 'practical' | 'psychological' | 'chakra';
  question: string;
  correctAnswer: string;
  options: string[];
  crystalId: string;
  explanation: string;
  xpValue: number;
}

// Practical everyday situations mapped to crystals
export const practicalScenarios: { scenario: string; crystalId: string; explanation: string }[] = [
  { scenario: "You have a job interview tomorrow and need confidence", crystalId: "citrine", explanation: "Citrine activates the solar plexus chakra, your personal power centre, dissolving self-doubt and igniting your inner fire." },
  { scenario: "You're feeling anxious and can't calm your racing thoughts", crystalId: "amethyst", explanation: "Amethyst's calming vibration quiets the overactive mind. Its natural lithium content helps ease anxious thoughts." },
  { scenario: "You're struggling to fall asleep at night", crystalId: "howlite", explanation: "Howlite calms an overactive mind and eases the transition into sleep, helping release worrying thoughts." },
  { scenario: "You need protection from negative energy at work", crystalId: "black-tourmaline", explanation: "Black tourmaline creates an energetic force field that repels negative energies and EMF radiation." },
  { scenario: "You want to attract more abundance and opportunities", crystalId: "green-aventurine", explanation: "Green aventurine is the 'Stone of Opportunity' - it attracts luck, prosperity, and new opportunities." },
  { scenario: "You're going through heartbreak and need emotional healing", crystalId: "rose-quartz", explanation: "Rose quartz is THE heart stone, dissolving emotional wounds and opening you to self-love and healing." },
  { scenario: "You need mental clarity for an important decision", crystalId: "clear-quartz", explanation: "Clear quartz clears mental fog, enhances concentration, and brings crystal-clear thinking." },
  { scenario: "You're feeling creatively blocked", crystalId: "carnelian", explanation: "Carnelian ignites the sacral chakra, boosting creativity, motivation, and artistic expression." },
  { scenario: "You need courage to speak your truth", crystalId: "lapis-lazuli", explanation: "Lapis lazuli activates the throat chakra, encouraging honest communication and authentic self-expression." },
  { scenario: "You're overwhelmed by other people's emotions", crystalId: "labradorite", explanation: "Labradorite creates a shield against energy vampires and emotional overwhelm while enhancing intuition." },
  { scenario: "You're struggling with low energy and fatigue", crystalId: "carnelian", explanation: "Carnelian's warm, fiery energy revitalises the body and restores vitality and motivation." },
  { scenario: "You need grounding after feeling scattered", crystalId: "smoky-quartz", explanation: "Smoky quartz is one of the most effective grounding stones, anchoring you to the present moment." },
  { scenario: "You want to enhance your intuition and psychic abilities", crystalId: "amethyst", explanation: "Amethyst opens the third eye chakra, enhancing meditation and psychic awareness." },
  { scenario: "You're dealing with anger and need to calm down", crystalId: "lepidolite", explanation: "Lepidolite contains natural lithium and is excellent for calming intense emotions including anger." },
  { scenario: "You need focus for studying or important work", crystalId: "fluorite", explanation: "Fluorite is the 'Genius Stone' - it clears mental clutter and enhances concentration and learning." },
  { scenario: "You're feeling disconnected from your body", crystalId: "obsidian", explanation: "Obsidian grounds spiritual energy into the physical body, creating a strong connection to earth." },
  { scenario: "You want to boost your self-esteem", crystalId: "tigers-eye", explanation: "Tiger's eye combines earth energy with sun energy, building confidence and personal power." },
  { scenario: "You're facing financial stress and need abundance energy", crystalId: "pyrite", explanation: "Pyrite is the 'Fool's Gold' that attracts real wealth through confidence and manifestation power." },
  { scenario: "You need to set better boundaries with others", crystalId: "amazonite", explanation: "Amazonite empowers authentic expression and helps establish healthy boundaries with compassion." },
  { scenario: "You're preparing for meditation or spiritual practice", crystalId: "selenite", explanation: "Selenite opens crown chakra connections to higher consciousness and cleanses all other crystals." },
];

// Psychological conditions and recommended crystals
export const psychologicalSupport: { condition: string; crystalId: string; explanation: string }[] = [
  { condition: "generalised anxiety", crystalId: "lepidolite", explanation: "Lepidolite contains natural lithium (used in mood-stabilising medications) and provides deep calming." },
  { condition: "depression and low mood", crystalId: "citrine", explanation: "Like captured sunshine, citrine radiates warm, joyful energy that lifts spirits and dispels darkness." },
  { condition: "panic attacks", crystalId: "black-tourmaline", explanation: "Black tourmaline grounds excess energy and creates a sense of safety and security during panic." },
  { condition: "social anxiety", crystalId: "rhodonite", explanation: "Rhodonite builds confidence in social situations while promoting forgiveness and self-acceptance." },
  { condition: "grief and loss", crystalId: "rose-quartz", explanation: "Rose quartz provides nurturing comfort during grief, promoting gentle healing and self-compassion." },
  { condition: "insomnia from racing thoughts", crystalId: "howlite", explanation: "Howlite calms the mind and slows racing thoughts, facilitating the transition to sleep." },
  { condition: "trauma and PTSD", crystalId: "obsidian", explanation: "Obsidian helps process and release deep-seated trauma stored in the body, promoting healing." },
  { condition: "emotional overwhelm", crystalId: "moonstone", explanation: "Moonstone balances emotional extremes and helps process feelings without becoming overwhelmed." },
  { condition: "low self-worth", crystalId: "sunstone", explanation: "Sunstone radiates confidence and self-worth, helping you recognise your inherent value." },
  { condition: "burnout and exhaustion", crystalId: "selenite", explanation: "Selenite cleanses energetic blockages and restores flow, helping recover from burnout." },
  { condition: "difficulty expressing emotions", crystalId: "amazonite", explanation: "Amazonite opens the throat chakra and empowers authentic emotional expression." },
  { condition: "obsessive thoughts", crystalId: "fluorite", explanation: "Fluorite clears mental clutter and helps organise thoughts, reducing obsessive patterns." },
  { condition: "fear and phobias", crystalId: "tigers-eye", explanation: "Tiger's eye builds courage and resilience, helping face fears with confidence." },
  { condition: "emotional numbness", crystalId: "carnelian", explanation: "Carnelian's warm energy reconnects you to your feelings and restores emotional vitality." },
  { condition: "codependency", crystalId: "malachite", explanation: "Malachite encourages healthy boundaries and personal transformation, breaking unhealthy patterns." },
];

// Generate quiz questions dynamically
export function generateQuizQuestions(count: number = 5): QuizQuestion[] {
  const questions: QuizQuestion[] = [];
  const usedTypes: string[] = [];
  
  // Mix of question types
  const questionTypes = ['practical', 'psychological', 'chakra', 'property', 'name'];
  
  for (let i = 0; i < count; i++) {
    const type = questionTypes[i % questionTypes.length] as QuizQuestion['type'];
    
    if (type === 'practical') {
      const scenario = practicalScenarios[Math.floor(Math.random() * practicalScenarios.length)];
      const crystal = crystals.find(c => c.id === scenario.crystalId);
      if (!crystal) continue;
      
      // Generate wrong options from other crystals
      const wrongOptions = crystals
        .filter(c => c.id !== scenario.crystalId)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(c => c.name);
      
      const options = [...wrongOptions, crystal.name].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: `practical-${i}`,
        type: 'practical',
        question: scenario.scenario,
        correctAnswer: crystal.name,
        options,
        crystalId: crystal.id,
        explanation: scenario.explanation,
        xpValue: 15
      });
    } else if (type === 'psychological') {
      const condition = psychologicalSupport[Math.floor(Math.random() * psychologicalSupport.length)];
      const crystal = crystals.find(c => c.id === condition.crystalId);
      if (!crystal) continue;
      
      const wrongOptions = crystals
        .filter(c => c.id !== condition.crystalId)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(c => c.name);
      
      const options = [...wrongOptions, crystal.name].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: `psychological-${i}`,
        type: 'psychological',
        question: `Which crystal would you recommend for someone experiencing ${condition.condition}?`,
        correctAnswer: crystal.name,
        options,
        crystalId: crystal.id,
        explanation: condition.explanation,
        xpValue: 20
      });
    } else if (type === 'chakra') {
      const crystal = crystals[Math.floor(Math.random() * crystals.length)];
      const primaryChakra = crystal.chakras[0];
      
      const allChakras = ['Root', 'Sacral', 'Solar Plexus', 'Heart', 'Throat', 'Third Eye', 'Crown'];
      const wrongChakras = allChakras
        .filter(c => !crystal.chakras.includes(c))
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      
      const options = [...wrongChakras, primaryChakra].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: `chakra-${i}`,
        type: 'chakra',
        question: `What is the primary chakra associated with ${crystal.name}?`,
        correctAnswer: primaryChakra,
        options,
        crystalId: crystal.id,
        explanation: `${crystal.name} is strongly connected to the ${primaryChakra} chakra, supporting ${crystal.properties[0].toLowerCase()}.`,
        xpValue: 10
      });
    } else if (type === 'property') {
      const crystal = crystals[Math.floor(Math.random() * crystals.length)];
      const correctProperty = crystal.properties[0];
      
      // Get properties from other crystals as wrong answers
      const wrongProperties = crystals
        .filter(c => c.id !== crystal.id)
        .flatMap(c => c.properties)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      
      const options = [...wrongProperties, correctProperty].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: `property-${i}`,
        type: 'property',
        question: `Which is a key property of ${crystal.name}?`,
        correctAnswer: correctProperty,
        options,
        crystalId: crystal.id,
        explanation: `${crystal.name} is known for: ${crystal.properties.slice(0, 2).join(', ')}.`,
        xpValue: 10
      });
    } else if (type === 'name') {
      const crystal = crystals[Math.floor(Math.random() * crystals.length)];
      
      const wrongNames = crystals
        .filter(c => c.id !== crystal.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(c => c.name);
      
      const options = [...wrongNames, crystal.name].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: `name-${i}`,
        type: 'name',
        question: `Which crystal "${crystal.description.split('.')[0]}"?`,
        correctAnswer: crystal.name,
        options,
        crystalId: crystal.id,
        explanation: crystal.description.split('.').slice(0, 2).join('.') + '.',
        xpValue: 10
      });
    }
  }
  
  return questions.sort(() => Math.random() - 0.5);
}

// Level progression
export const levelRequirements = [
  { level: 1, xpRequired: 0, title: 'Crystal Curious' },
  { level: 2, xpRequired: 100, title: 'Stone Seeker' },
  { level: 3, xpRequired: 300, title: 'Mineral Explorer' },
  { level: 4, xpRequired: 600, title: 'Gem Enthusiast' },
  { level: 5, xpRequired: 1000, title: 'Crystal Keeper' },
  { level: 6, xpRequired: 1500, title: 'Stone Scholar' },
  { level: 7, xpRequired: 2100, title: 'Crystal Healer' },
  { level: 8, xpRequired: 2800, title: 'Gem Master' },
  { level: 9, xpRequired: 3600, title: 'Crystal Sage' },
  { level: 10, xpRequired: 4500, title: 'Crystal Alchemist' },
];

export function getLevelInfo(xp: number) {
  let currentLevel = levelRequirements[0];
  let nextLevel = levelRequirements[1];
  
  for (let i = levelRequirements.length - 1; i >= 0; i--) {
    if (xp >= levelRequirements[i].xpRequired) {
      currentLevel = levelRequirements[i];
      nextLevel = levelRequirements[i + 1] || levelRequirements[i];
      break;
    }
  }
  
  const xpInCurrentLevel = xp - currentLevel.xpRequired;
  const xpForNextLevel = nextLevel.xpRequired - currentLevel.xpRequired;
  const progress = xpForNextLevel > 0 ? (xpInCurrentLevel / xpForNextLevel) * 100 : 100;
  
  return {
    level: currentLevel.level,
    title: currentLevel.title,
    xpInCurrentLevel,
    xpForNextLevel,
    progress: Math.min(progress, 100),
    nextTitle: nextLevel.title
  };
}
